import type { EligibleStats } from "../../domain/contracts/http";
import type { PackageNotificationTriggerType } from "../../domain/contracts/triggers";
import { Badge, Button, Input, cn } from "@devhop/ui";
import { SearchIcon, XIcon } from "lucide-react";
import type {
  EligibleNotificationsFilters,
  EligibleNotificationsPackageFilter,
  EligibleNotificationsTriggerFilter,
} from "../lib/eligible-notifications-filters";
import { TRIGGER_LABELS } from "./WorkerStatusBanner";

const PACKAGE_FILTER_OPTIONS: {
  key: EligibleNotificationsPackageFilter;
  label: string;
}[] = [
  { key: "all", label: "ແພັກທັງໝົດ" },
  { key: "package", label: "ຄລາສ" },
  { key: "private_lesson", label: "ສ່ວນຕົວ" },
];

const TRIGGER_FILTER_OPTIONS: {
  key: EligibleNotificationsTriggerFilter;
  label: string;
}[] = [
  { key: "all", label: "trigger ທັງໝົດ" },
  ...(
    [
      "low_sessions",
      "low_hours",
      "expiring_soon",
      "combined",
    ] as PackageNotificationTriggerType[]
  ).map((key) => ({
    key,
    label: TRIGGER_LABELS[key] ?? key,
  })),
];

type EligibleNotificationsFilterBarProps = {
  filters: EligibleNotificationsFilters;
  onFiltersChange: (next: EligibleNotificationsFilters) => void;
  stats?: EligibleStats;
  filteredTotal: number;
  totalAll: number;
};

export function EligibleNotificationsFilterBar({
  filters,
  onFiltersChange,
  stats,
  filteredTotal,
  totalAll,
}: EligibleNotificationsFilterBarProps) {
  const packageCounts = {
    package: stats?.byPackageType?.package ?? 0,
    private_lesson: stats?.byPackageType?.private_lesson ?? 0,
  };
  const triggerCounts = {
    low_sessions: stats?.byTriggerType?.low_sessions ?? 0,
    low_hours: stats?.byTriggerType?.low_hours ?? 0,
    expiring_soon: stats?.byTriggerType?.expiring_soon ?? 0,
    combined: stats?.byTriggerType?.combined ?? 0,
  };
  const safeFilteredTotal = filteredTotal ?? 0;
  const safeTotalAll = totalAll ?? packageCounts.package + packageCounts.private_lesson;

  const hasActiveFilters =
    filters.search.trim() !== "" ||
    filters.packageType !== "all" ||
    filters.triggerType !== "all";

  const resetFilters = () => {
    onFiltersChange({
      search: "",
      packageType: "all",
      triggerType: "all",
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative w-full lg:max-w-sm">
          <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filters.search ?? ""}
            onChange={(event) =>
              onFiltersChange({ ...filters, search: event.target.value })
            }
            placeholder="ຄົ້ນຫານັກຮຽນ, Member ID, ແພັກ, ເບີ..."
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground text-xs tabular-nums">
            ກັ່ນຕົວ {safeFilteredTotal.toLocaleString("lo-LA")} /{" "}
            {safeTotalAll.toLocaleString("lo-LA")} ລາຍການ
          </span>
          {hasActiveFilters ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 gap-1"
              onClick={resetFilters}
            >
              <XIcon className="size-3.5" />
              ລ້າງກັ່ນຕົວ
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-xs">ແພັກ:</span>
        {PACKAGE_FILTER_OPTIONS.map((opt) => {
          const count =
            opt.key === "all"
              ? safeTotalAll
              : (packageCounts[opt.key as "package" | "private_lesson"] ?? 0);
          const active = filters.packageType === opt.key;

          return (
            <Button
              key={opt.key}
              type="button"
              size="sm"
              variant={active ? "default" : "outline"}
              className="h-8 gap-1.5"
              onClick={() =>
                onFiltersChange({ ...filters, packageType: opt.key })
              }
            >
              {opt.label}
              <Badge
                variant={active ? "secondary" : "outline"}
                className="h-5 min-w-5 px-1 text-[10px] tabular-nums"
              >
                {(count ?? 0).toLocaleString("lo-LA")}
              </Badge>
            </Button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-xs">Trigger:</span>
        {TRIGGER_FILTER_OPTIONS.map((opt) => {
          const count =
            opt.key === "all"
              ? safeTotalAll
              : (triggerCounts[opt.key as PackageNotificationTriggerType] ?? 0);
          const active = filters.triggerType === opt.key;

          return (
            <Button
              key={opt.key}
              type="button"
              size="sm"
              variant={active ? "default" : "outline"}
              className={cn(
                "h-8 gap-1.5",
                opt.key === "expiring_soon" &&
                  !active &&
                  "border-amber-500/40 text-amber-800 hover:bg-amber-500/10",
              )}
              onClick={() =>
                onFiltersChange({ ...filters, triggerType: opt.key })
              }
            >
              {opt.label}
              <Badge
                variant={active ? "secondary" : "outline"}
                className="h-5 min-w-5 px-1 text-[10px] tabular-nums"
              >
                {(count ?? 0).toLocaleString("lo-LA")}
              </Badge>
            </Button>
          );
        })}
      </div>
    </div>
  );
}
